import {match, Pattern} from 'ts-pattern';

import {useRef, useState} from 'react';

import {equals, isNotNil} from 'ramda';

import {aiAssistantContextStore} from '@dms/ai-assistant';

import type {AssistantRentalOrderContext} from '@prompt-bridge/shared/ai-assistant-core';

import {useLatest} from 'shared/web';

const CONTEXT_REFRESH_INTERVAL_MS = 60_000;
type ContextOwnerToken = object;
type CreateContext = () => AssistantRentalOrderContext | undefined;

let activeRentalOrderContextOwner: ContextOwnerToken | undefined;

export function usePublishAssistantRentalOrderContext(
  createContext: CreateContext,
  refreshContext: () => Promise<object[]>
) {
  const latestCreateContext = useLatest(createContext);
  const latestRefreshContext = useLatest(refreshContext);
  const lastPublished = useRef<AssistantRentalOrderContext | undefined>(undefined);
  const refreshInterval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const unregisterSendSynchronizer = useRef<(() => void) | undefined>(undefined);
  const [ownerToken] = useState<ContextOwnerToken>(() => ({}));
  const [publish] = useState(
    () =>
      (nextContext: AssistantRentalOrderContext | undefined, force = false) => {
        const ownsContext = activeRentalOrderContextOwner === ownerToken;
        if (!force && ownsContext && equals(lastPublished.current, nextContext)) {
          return;
        }

        lastPublished.current = nextContext;
        match({nextContext, ownsContext})
          .with({nextContext: Pattern.nullish, ownsContext: false}, () => undefined)
          .with({nextContext: Pattern.nullish, ownsContext: true}, () => {
            activeRentalOrderContextOwner = undefined;
            aiAssistantContextStore.patchContext({rentalOrder: undefined});
          })
          .with({nextContext: Pattern.nonNullable}, ({nextContext}) => {
            activeRentalOrderContextOwner = ownerToken;
            aiAssistantContextStore.patchContext({rentalOrder: nextContext});
          })
          .exhaustive();
      }
  );
  const [ownerRef] = useState(() => (node: HTMLDivElement | null) => {
    if (isNotNil(node)) {
      publish(latestCreateContext.current());
      clearInterval(refreshInterval.current);
      refreshInterval.current = setInterval(() => {
        const publishIfMounted = () => {
          if (isNotNil(refreshInterval.current)) {
            publish(latestCreateContext.current());
          }
        };
        void latestRefreshContext.current().then(publishIfMounted, publishIfMounted);
      }, CONTEXT_REFRESH_INTERVAL_MS);
      unregisterSendSynchronizer.current?.();
      unregisterSendSynchronizer.current = aiAssistantContextStore.registerSendSynchronizer(() =>
        publish(latestCreateContext.current(), true)
      );
      return;
    }

    clearInterval(refreshInterval.current);
    refreshInterval.current = undefined;
    unregisterSendSynchronizer.current?.();
    unregisterSendSynchronizer.current = undefined;
    if (activeRentalOrderContextOwner === ownerToken) {
      activeRentalOrderContextOwner = undefined;
      lastPublished.current = undefined;
      aiAssistantContextStore.patchContext({rentalOrder: undefined});
    }
  });
  const synchronizeRef = (node: HTMLSpanElement | null) => {
    if (isNotNil(node)) {
      publish(createContext());
    }
  };

  return {ownerRef, synchronizeRef};
}
